import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../../i18n';

// Sammelfotos: viele Belege nacheinander fotografieren und am Ende zusammen hochladen.
// Mit Kamera-Zugriff (getUserMedia) als Live-Bild mit Auslöser; sonst Schleife über die Kamera-App des Handys.
// Fotos werden auf höchstens 3000 px verkleinert und als JPEG (Qualität 0,85) gespeichert, scharf genug, dass auch
// kleine Schrift auf Kassenbons lesbar bleibt (Gesetzes-Prüfung C5).

const MAX_PX = 3000;
const QUALITY = 0.85;
// So lange darf toBlob brauchen; danach wird das Foto direkt (toDataURL) erzeugt
const BLOB_WAIT_MS = 2000;

interface Shot {
  blob: Blob;
  url: string;
}

export function Sammelfotos({ baseName, offset, inHistory, onBack, onDone, onClose }: {
  baseName: string; // z. B. „Eingangsbelege_September“
  offset: number; // so viele Belege liegen schon im Ordner, die Nummern zählen weiter
  inHistory: boolean; // der eigene Verlaufseintrag ist da (weg = Zurück-Geste)
  onBack(): void; // Verlaufseintrag wieder anlegen (Fotos bleiben, es wird erst gefragt)
  onDone(files: File[]): void;
  onClose(): void;
}) {
  const [shots, setShots] = useState<Shot[]>([]);
  const [live, setLive] = useState<boolean | null>(null); // null: Kamera startet noch
  // Live-Bild hat seine Größe: erst dann ist der Auslöser frei (sonst ginge ein Tippen ins Leere)
  const [ready, setReady] = useState(false);
  const [missed, setMissed] = useState(false); // letztes Auslösen ohne Foto: Hinweis statt stillem Nichts
  const [pending, setPending] = useState(0); // ausgelöst, Foto wird noch erzeugt
  const [ask, setAsk] = useState(false);
  const [flash, setFlash] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const strip = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const shotsRef = useRef(shots);
  shotsRef.current = shots;

  useEffect(() => {
    let stopped = false;
    const md = navigator.mediaDevices;
    if (!md?.getUserMedia) {
      setLive(false);
    } else {
      md.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } }, audio: false })
        .then((s) => {
          if (stopped) {
            s.getTracks().forEach((tr) => tr.stop());
            return;
          }
          stream.current = s;
          setLive(true);
        })
        .catch(() => {
          if (!stopped) setLive(false);
        });
    }
    const app = document.querySelector<HTMLElement>('.mapp > .app');
    app?.setAttribute('inert', '');
    return () => {
      stopped = true;
      stream.current?.getTracks().forEach((tr) => tr.stop());
      app?.removeAttribute('inert');
      for (const s of shotsRef.current) URL.revokeObjectURL(s.url);
    };
  }, []);

  useEffect(() => {
    const v = video.current;
    if (!live || !v) return;
    // bereit = Größe bekannt und ein Bild da; Ereignisse und zur Sicherheit regelmäßig nachsehen
    const check = () => {
      if (v.videoWidth > 0 && v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        setReady(true);
        clearInterval(poll);
      }
    };
    const poll = setInterval(check, 300);
    const events = ['loadedmetadata', 'loadeddata', 'canplay', 'playing', 'resize'];
    for (const e of events) v.addEventListener(e, check);
    if (stream.current && v.srcObject !== stream.current) {
      v.srcObject = stream.current;
      v.play().catch(() => {});
    }
    check();
    return () => {
      clearInterval(poll);
      for (const e of events) v.removeEventListener(e, check);
    };
  }, [live]);

  useEffect(() => {
    if (strip.current) strip.current.scrollLeft = strip.current.scrollWidth;
  }, [shots.length]);

  // Zurück-Geste: ohne Fotos schließen, sonst wie „Verwerfen“ erst nachfragen und den Eintrag wieder anlegen
  const wasInHistory = useRef(false);
  useEffect(() => {
    if (inHistory) {
      wasInHistory.current = true;
      return;
    }
    if (!wasInHistory.current) return;
    wasInHistory.current = false;
    if (shotsRef.current.length) {
      onBack();
      setAsk(true);
    } else onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inHistory]);

  const n = shots.length;
  const cancel = () => (n ? setAsk(true) : onClose());
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (shotsRef.current.length) setAsk(true);
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Jedes Auslösen endet mit einem Foto oder einem sichtbaren Hinweis, nie still
  const shoot = () => {
    const v = video.current;
    if (!v || !v.videoWidth || v.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      setMissed(true);
      return;
    }
    setPending((x) => x + 1);
    void grab(v).then((b) => {
      setPending((x) => x - 1);
      if (!b) {
        setMissed(true);
        return;
      }
      setMissed(false);
      setShots((s) => [...s, { blob: b, url: URL.createObjectURL(b) }]);
    });
    // Rückmeldung erst danach: ein Fehler beim Vibrieren darf das Foto nicht verhindern
    setFlash(true);
    setTimeout(() => setFlash(false), 60);
    try {
      navigator.vibrate?.(30);
    } catch {
      // manche Browser erlauben kein Vibrieren
    }
  };

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    for (const f of Array.from(files)) {
      const blob = await shrink(f);
      setShots((s) => [...s, { blob, url: URL.createObjectURL(blob) }]);
    }
  };

  const remove = (i: number) =>
    setShots((s) => {
      const x = s[i];
      if (x) URL.revokeObjectURL(x.url);
      return s.filter((_, j) => j !== i);
    });

  const done = () => {
    if (!n) return;
    const files = shots.map((x, i) => {
      const type = x.blob.type || 'image/jpeg';
      const ext = type === 'image/jpeg' ? 'jpg' : (type.split('/')[1] ?? 'jpg');
      const name = `${baseName}_${String(offset + i + 1).padStart(2, '0')}.${ext}`.replace(/[\s/]+/g, '_');
      return new File([x.blob], name, { type });
    });
    onDone(files);
  };

  const top = ask ? (
    <div className="sv-top sv-ask">
      <span>{n === 1 ? t('Das Foto verwerfen?') : t('Alle {n} Fotos verwerfen?', { n })}</span>
      <span className="sv-askb">
        <button type="button" onClick={onClose}>
          {t('Ja, verwerfen')}
        </button>
        <button type="button" className="sv-light" onClick={() => setAsk(false)} autoFocus>
          {t('Weiter fotografieren')}
        </button>
      </span>
    </div>
  ) : (
    <div className="sv-top">
      <span>
        {live === null
          ? t('Kamera wird gestartet …')
          : `${t('Sammelfotos')} · ${n === 1 ? t('1 Foto') : t('{n} Fotos', { n })}`}
      </span>
      <button type="button" onClick={cancel}>
        {n ? t('Verwerfen') : t('Abbrechen')}
      </button>
    </div>
  );

  const thumbs = shots.map((x, i) => (
    <div className="sv-th" key={x.url}>
      <img src={x.url} alt={t('Foto {n}', { n: i + 1 })} />
      <button type="button" aria-label={t('Foto {n} entfernen', { n: i + 1 })} onClick={() => remove(i)}>
        ×
      </button>
    </div>
  ));
  const doneBtn = (
    <button type="button" className="sv-done" disabled={!n || pending > 0} onClick={done}>
      {t('Fertig · {n} hochladen', { n })}
    </button>
  );

  const host = document.querySelector('.mapp') ?? document.body;
  return createPortal(
    <div className="serie" role="dialog" aria-modal="true" aria-label={t('Sammelfotos')}>
      {top}
      {live === true && (
        <>
          <div className="sv-live">
            <video ref={video} playsInline muted autoPlay />
            <div className={`flash${flash ? ' on' : ''}`} />
          </div>
          <div className="sv-strip" ref={strip}>
            {thumbs}
          </div>
          <div className="sv-bar">
            <span className="sv-n" aria-live="polite">
              {!ready
                ? t('Kamera wird vorbereitet …')
                : pending > 0
                  ? t('Foto wird gespeichert …')
                  : missed
                    ? t('Das Foto hat nicht geklappt. Bitte noch einmal auslösen.')
                    : t('Beleg flach hinlegen, dann auslösen')}
            </span>
            <button type="button" className="shutter" aria-label={t('Foto aufnehmen')} disabled={!ready} onClick={shoot} />
            <span className="sv-donebox">{doneBtn}</span>
          </div>
        </>
      )}
      {live === false && (
        <>
          <div className="sv-loop">
            <button type="button" className="sv-cam" onClick={() => input.current?.click()}>
              ◉ {n ? t('Nächstes Foto') : t('Erstes Foto aufnehmen')}
            </button>
            <small>{t('Nach jedem Foto kommen Sie hierher zurück. Wenn alle Belege fotografiert sind, auf „Fertig“ tippen.')}</small>
            {n > 0 && <div className="sv-grid">{thumbs}</div>}
          </div>
          <div className="sv-bar">
            <span />
            <span />
            {doneBtn}
          </div>
          <input
            ref={input}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            data-testid="sammel-input"
            onChange={(e) => {
              void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </>
      )}
    </div>,
    host,
  );
}

// Aktuelles Bild des Live-Videos als JPEG (höchstens 3000 px). toBlob bleibt auf manchen Geräten aus oder
// liefert nichts: dann nach kurzer Wartezeit direkt über toDataURL. null = kein Foto entstanden.
function grab(v: HTMLVideoElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    const c = document.createElement('canvas');
    try {
      const k = Math.min(1, MAX_PX / Math.max(v.videoWidth, v.videoHeight));
      c.width = Math.round(v.videoWidth * k);
      c.height = Math.round(v.videoHeight * k);
      const ctx = c.getContext('2d');
      if (!ctx) return resolve(null);
      ctx.drawImage(v, 0, 0, c.width, c.height);
    } catch {
      return resolve(null);
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const finish = (b: Blob | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(b && b.size > 0 ? b : fromDataUrl(c));
    };
    timer = setTimeout(() => finish(null), BLOB_WAIT_MS);
    try {
      c.toBlob(finish, 'image/jpeg', QUALITY);
    } catch {
      finish(null);
    }
  });
}

function fromDataUrl(c: HTMLCanvasElement): Blob | null {
  try {
    const url = c.toDataURL('image/jpeg', QUALITY);
    const m = /^data:(image\/[\w+.-]+);base64,(.+)$/.exec(url);
    if (!m) return null;
    const bin = atob(m[2]!);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: m[1] });
  } catch {
    return null;
  }
}

// Foto aus der Kamera-App verkleinern (höchstens 3000 px, JPEG 0,85). Kann der Browser es nicht lesen
// (z. B. HEIC), bleibt das Original.
async function shrink(file: File): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const k = Math.min(1, MAX_PX / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * k);
    c.height = Math.round(bmp.height * k);
    c.getContext('2d')?.drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close();
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', QUALITY));
    return blob ?? file;
  } catch {
    return file;
  }
}
